import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useUser } from '../../contexts/UserContext';
import PermissionDeniedPage from '../../pages/PermissionDeniedPage';
import {
  AccountRequest,
  AccountRequestAction,
  AccountRequestTab,
  decideAccountRequest,
  listAccountRequests,
  updateAccountRequest,
} from '../../services/api/accountRequests.service';
import ArtistAnalyticsModal from './ArtistAnalyticsModal';

const PAGE_SIZE = 25;

const emptyEdit = (): Partial<AccountRequest> => ({
  firstname: '',
  lastname: '',
  mobile: '',
  date_of_birth: '',
  artist_name: '',
  distributor: '',
  spotify_url: '',
  instagram_url: '',
  apple_url: '',
  youtube_url: '',
  tiktok_url: '',
  deezer_url: '',
  amazon_url: '',
});

const statusBadge = (status: string) => {
  const base = 'inline-flex rounded-full px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-wide';
  if (status === 'pending') return `${base} bg-amber-100 text-amber-800`;
  if (status === 'waitlisted') return `${base} bg-blue-100 text-blue-800`;
  if (status === 'approved') return `${base} bg-green-100 text-green-800`;
  if (status === 'rejected') return `${base} bg-red-100 text-red-800`;
  return `${base} bg-gray-100 text-gray-700`;
};

const linkOrDash = (url?: string | null, label?: string) => {
  if (!url) return label ? null : <span className="text-gray-400">—</span>;
  const urls = url.split(',').map((item) => item.trim()).filter(Boolean);
  return <>{urls.map((item, index) => (
    <a
      key={`${item}-${index}`}
      href={item}
      target="_blank"
      rel="noreferrer"
      className="block max-w-[14rem] truncate text-blue-700 hover:underline"
      title={item}
    >
      {label ? `${label}${urls.length > 1 ? ` ${index + 1}` : ''}: ${item}` : item}
    </a>
  ))}</>;
};

const AccountRequests: React.FC = () => {
  const { userProfile } = useUser();
  const systemRole = (() => {
    try { return sessionStorage.getItem('systemRole') || userProfile?.systemRole || null; }
    catch { return null; }
  })();
  const isSystemAdmin = systemRole === 'system_admin';

  const [tab, setTab] = useState<AccountRequestTab>('pending');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState<AccountRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Partial<AccountRequest>>(emptyEdit());
  const [analyticsRequest, setAnalyticsRequest] = useState<AccountRequest | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listAccountRequests({
        tab,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setRows(data.requests || []);
      setTotal(data.total || 0);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load account requests');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [tab, page]);

  useEffect(() => {
    if (isSystemAdmin) void load();
  }, [isSystemAdmin, load]);

  useEffect(() => { setPage(0); setEditingId(null); }, [tab]);

  if (!isSystemAdmin) return <PermissionDeniedPage />;

  const openEdit = (r: AccountRequest) => {
    setEditingId(r.id);
    setDraft({
      firstname: r.firstname || '',
      lastname: r.lastname || '',
      mobile: r.mobile || '',
      date_of_birth: r.date_of_birth ? String(r.date_of_birth).slice(0, 10) : '',
      artist_name: r.artist_name || '',
      distributor: r.distributor || '',
      spotify_url: r.spotify_url || '',
      instagram_url: r.instagram_url || '',
      apple_url: r.apple_url || '',
      youtube_url: r.youtube_url || '',
      tiktok_url: r.tiktok_url || '',
      deezer_url: r.deezer_url || '',
      amazon_url: r.amazon_url || '',
    });
  };

  const saveEdit = async () => {
    if (editingId == null) return;
    try {
      setBusyId(editingId);
      await updateAccountRequest(editingId, {
        firstname: String(draft.firstname || ''),
        lastname: String(draft.lastname || ''),
        mobile: String(draft.mobile || ''),
        date_of_birth: String(draft.date_of_birth || ''),
        artist_name: String(draft.artist_name || ''),
        distributor: String(draft.distributor || ''),
        spotify_url: String(draft.spotify_url || ''),
        instagram_url: String(draft.instagram_url || ''),
        apple_url: String(draft.apple_url || ''),
        youtube_url: String(draft.youtube_url || ''),
        tiktok_url: String(draft.tiktok_url || ''),
        deezer_url: String(draft.deezer_url || ''),
        amazon_url: String(draft.amazon_url || ''),
      });
      toast.success('Request updated');
      setEditingId(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update request');
    } finally {
      setBusyId(null);
    }
  };

  const decide = async (id: number, action: AccountRequestAction): Promise<boolean> => {
    try {
      setBusyId(id);
      await decideAccountRequest(id, action);
      const successMessages: Record<AccountRequestAction, string> = {
        approve: 'Request approved',
        reject: 'Request rejected',
        waitlist: 'Request moved to waiting list',
        pending: 'Request moved to pending',
      };
      toast.success(successMessages[action]);
      if (editingId === id) setEditingId(null);
      await load();
      return true;
    } catch (error: any) {
      toast.error(error?.message || `Failed to ${action} request`);
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const lastPage = pageCount - 1;

  const editInput = (key: keyof AccountRequest, label: string, type = 'text') => (
    <label className="block text-[0.65rem] text-gray-500">
      <span className="mb-1 block font-medium uppercase tracking-wide">{label}</span>
      <input
        type={type}
        className="w-full rounded border border-gray-200 bg-white px-2.5 py-1.5 text-[0.8rem] text-gray-900 focus:border-gray-400 focus:outline-none"
        value={(draft[key] as string) || ''}
        onChange={(e) => setDraft((d: Partial<AccountRequest>) => ({ ...d, [key]: e.target.value }))}
      />
    </label>
  );

  return (
    <div className="mx-auto w-full max-w-6xl p-4 md:p-6 font-family-messina-sans-regular">
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-gray-900">User account requests</h1>
        <p className="mt-1 text-[0.75rem] text-gray-500">
          Review signup requests. Edit details before approve. Email cannot be changed.
        </p>
      </div>

      <div className="mb-4 flex gap-2">
        {(['pending', 'waitlisted', 'completed'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-lg px-3 py-1.5 text-[0.7rem] font-medium uppercase tracking-wide transition-colors ${
              tab === t
                ? 'bg-black text-white'
                : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t === 'waitlisted' ? 'Waiting List' : t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-[0.8rem] text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 py-16 text-center text-[0.8rem] text-gray-500">
          No {tab} requests.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white">
          <table className="w-full min-w-[960px] text-[0.75rem]">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-3 py-2.5 text-left font-medium">Name</th>
                <th className="px-3 py-2.5 text-left font-medium">Contact</th>
                <th className="px-3 py-2.5 text-left font-medium">Artist / Distributor</th>
                <th className="px-3 py-2.5 text-left font-medium">Links</th>
                <th className="px-3 py-2.5 text-left font-medium">Submitted</th>
                <th className="px-3 py-2.5 text-left font-medium">Status</th>
                <th className="px-3 py-2.5 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isEditing = editingId === r.id;
                const busy = busyId === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <tr className="border-t border-gray-100 align-top hover:bg-gray-50/60">
                      <td className="px-3 py-3">
                        <div className="font-medium text-gray-900">{r.name || '—'}</div>
                        <div className="mt-0.5 text-[0.7rem] text-gray-500">
                          DOB {r.date_of_birth ? String(r.date_of_birth).slice(0, 10) : '—'}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="max-w-[12rem] truncate text-gray-800" title={r.email}>{r.email}</div>
                        <div className="mt-0.5 text-gray-500">{r.mobile || '—'}</div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-gray-800">{r.artist_name || '—'}</div>
                        <div className="mt-0.5 text-gray-500">{r.distributor || '—'}</div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="space-y-0.5">
                          {linkOrDash(r.spotify_url)}
                          {linkOrDash(r.instagram_url, 'IG')}
                          {linkOrDash(r.apple_url, 'Apple')}
                          {linkOrDash(r.youtube_url, 'YT')}
                          {linkOrDash(r.tiktok_url, 'TT')}
                          {linkOrDash(r.deezer_url, 'Deezer')}
                          {linkOrDash(r.amazon_url, 'Amazon')}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-gray-600">
                        {new Date(r.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-3">
                        <span className={statusBadge(r.account_approval_status)}>
                          {r.account_approval_status}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          {tab === 'pending' && (
                            <>
                              <button
                                type="button"
                                disabled={busy || !r.spotify_url}
                                onClick={() => setAnalyticsRequest(r)}
                                className="rounded-lg border border-violet-200 bg-violet-50 p-1.5 text-violet-700 hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-40"
                                title={r.spotify_url ? 'View Spotify and Instagram analytics' : 'Spotify URL required'}
                                aria-label="View artist analytics"
                              >
                                <BarChart3 size={14} />
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => (isEditing ? setEditingId(null) : openEdit(r))}
                                className="rounded-lg border border-gray-200 px-2.5 py-1 text-[0.7rem] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              >
                                {isEditing ? 'Close' : 'Edit'}
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => decide(r.id, 'waitlist')}
                                className="rounded-lg border border-blue-200 px-2.5 py-1 text-[0.7rem] font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                              >
                                {busy ? '…' : 'Waitlist'}
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => decide(r.id, 'approve')}
                                className="rounded-lg bg-black px-2.5 py-1 text-[0.7rem] font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                              >
                                {busy ? '…' : 'Approve'}
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => decide(r.id, 'reject')}
                                className="rounded-lg border border-red-200 px-2.5 py-1 text-[0.7rem] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {tab === 'waitlisted' && (
                            <>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => decide(r.id, 'pending')}
                                className="rounded-lg border border-amber-200 px-2.5 py-1 text-[0.7rem] font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                              >
                                {busy ? '…' : 'Move to Pending'}
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => decide(r.id, 'approve')}
                                className="rounded-lg bg-black px-2.5 py-1 text-[0.7rem] font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                              >
                                {busy ? '…' : 'Approve'}
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => decide(r.id, 'reject')}
                                className="rounded-lg border border-red-200 px-2.5 py-1 text-[0.7rem] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {tab === 'completed' && r.account_approval_status === 'rejected' && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => decide(r.id, 'approve')}
                              className="rounded-lg bg-black px-2.5 py-1 text-[0.7rem] font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                            >
                              {busy ? '…' : 'Re-approve'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {isEditing && (
                      <tr className="border-t border-gray-100 bg-gray-50/80">
                        <td colSpan={7} className="px-3 py-4">
                          <div className="mb-3 text-[0.7rem] font-medium uppercase tracking-wide text-gray-500">
                            Edit request details
                          </div>
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {editInput('firstname', 'First name')}
                            {editInput('lastname', 'Last name')}
                            <label className="block text-[0.65rem] text-gray-500">
                              <span className="mb-1 block font-medium uppercase tracking-wide">Email (read-only)</span>
                              <input
                                className="w-full cursor-not-allowed rounded border border-gray-100 bg-gray-100 px-2.5 py-1.5 text-[0.8rem] text-gray-600"
                                value={r.email}
                                disabled
                              />
                            </label>
                            {editInput('mobile', 'Phone')}
                            {editInput('date_of_birth', 'Date of birth', 'date')}
                            {editInput('artist_name', 'Artist name')}
                            {editInput('distributor', 'Distributor')}
                            {editInput('spotify_url', 'Spotify')}
                            {editInput('instagram_url', 'Instagram')}
                            {editInput('apple_url', 'Apple')}
                            {editInput('youtube_url', 'YouTube')}
                            {editInput('tiktok_url', 'TikTok')}
                            {editInput('deezer_url', 'Deezer')}
                            {editInput('amazon_url', 'Amazon Music')}
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={busy}
                              onClick={saveEdit}
                              className="rounded-lg bg-black px-3 py-1.5 text-[0.7rem] font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                            >
                              Save changes
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => setEditingId(null)}
                              className="rounded-lg border border-gray-200 px-3 py-1.5 text-[0.7rem] font-medium text-gray-700 hover:bg-white disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-3 text-[0.75rem] text-gray-600">
        <span>
          {total === 0
            ? '0 of 0'
            : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="rounded-lg border border-gray-200 px-2.5 py-1 disabled:opacity-40"
          >
            ← Prev
          </button>
          <span className="px-1">Page {page + 1} of {pageCount}</span>
          <button
            type="button"
            disabled={page >= lastPage}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            className="rounded-lg border border-gray-200 px-2.5 py-1 disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      </div>
      <ArtistAnalyticsModal
        request={analyticsRequest}
        onClose={() => setAnalyticsRequest(null)}
        onDecision={decide}
      />
    </div>
  );
};

export default AccountRequests;
